import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * OfferBanner Component
 * 
 * A reusable promotional banner component for displaying discount codes and special offers.
 * Can be easily customized for different occasions (Christmas, New Year, Diwali, etc.)
 * 
 * @param {Object} props
 * @param {boolean} props.show - Controls visibility of the banner (default: false)
 * @param {string} props.emoji - Leading emoji (default: '🎄')
 * @param {string} props.couponCode - The discount code to display (default: 'SANTA2025')
 * @param {string} props.discountText - Discount description (default: 'for 15% OFF')
 * @param {string} props.offerText - Additional offer text (default: 'Limited Period Offer! 🎁')
 * @param {string} props.backgroundColor - Background gradient colors (default: Christmas theme)
 * @param {string} props.borderColor - Border color (default: gold)
 * @param {string} props.codeBackground - Coupon code background gradient (default: gold gradient)
 * @param {string} props.codeTextColor - Coupon code text color (default: '#c31432')
 * @param {string} props.discountTextColor - Discount text color (default: '#4CAF50')
 */
const OfferBanner = ({
  show = false,
  emoji = '🎄',
  couponCode = 'SANTA2025',
  discountText = 'for 15% OFF',
  offerText = 'Limited Period Offer! 🎁',
  backgroundColor = 'linear-gradient(135deg, rgba(195, 20, 50, 0.2) 0%, rgba(36, 11, 54, 0.2) 100%)',
  borderColor = 'rgba(255, 215, 0, 0.3)',
  codeBackground = 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
  codeTextColor = '#c31432',
  discountTextColor = '#4CAF50'
}) => {
  // Don't render if show is false
  if (!show) return null;

  return (
    <Box sx={{ 
      textAlign: 'center', 
      mb: 2,
      p: 2,
      background: backgroundColor,
      borderRadius: '12px',
      border: `2px solid ${borderColor}`,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255, 215, 0, 0.05) 10px, rgba(255, 215, 0, 0.05) 20px)',
        pointerEvents: 'none',
      }
    }}>
      <Typography sx={{ 
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: { xs: '0.85rem', sm: '0.95rem' },
        fontWeight: 600,
        mb: 1,
        textTransform: 'uppercase',
        letterSpacing: '1px'
      }}>
        {emoji} Use Code
      </Typography>
      <Box sx={{ 
        display: 'inline-block',
        position: 'relative',
        background: codeBackground,
        color: codeTextColor,
        padding: { xs: '8px 20px', sm: '10px 24px' },
        fontWeight: 900,
        fontSize: { xs: '1.1rem', sm: '1.3rem' },
        letterSpacing: '3px',
        transform: 'skew(-10deg)',
        boxShadow: '0 4px 15px rgba(255, 215, 0, 0.5), inset 0 -2px 0 rgba(0,0,0,0.2)',
        border: '2px solid #FFFFFF',
        borderLeft: '4px solid #FFFFFF',
        borderRight: '4px solid #FFFFFF',
        animation: 'pulse 2s ease-in-out infinite',
        '@keyframes pulse': {
          '0%, 100%': { transform: 'skew(-10deg) scale(1)' },
          '50%': { transform: 'skew(-10deg) scale(1.05)' }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '-8px',
          width: '0',
          height: '0',
          borderTop: '12px solid transparent',
          borderBottom: '12px solid transparent',
          borderRight: '8px solid #FFD700',
          transform: 'translateY(-50%)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '50%',
          right: '-8px',
          width: '0',
          height: '0',
          borderTop: '12px solid transparent',
          borderBottom: '12px solid transparent',
          borderLeft: '8px solid #FFA500',
          transform: 'translateY(-50%)',
        }
      }}>
        {couponCode}
      </Box>
      <Typography sx={{ 
        color: discountTextColor,
        fontSize: { xs: '0.8rem', sm: '0.9rem' },
        fontWeight: 700,
        mt: 1,
        textShadow: '0 2px 4px rgba(0,0,0,0.3)'
      }}>
        {discountText} • {offerText}
      </Typography>
    </Box>
  );
};

export default OfferBanner;
